/**
 * CitedSourcesChipStrip — parses `[cite:doc-id]` markers out of the lesson
 * body and shows each cited doc as a click-to-scroll chip below the editor.
 * "Remove" strips both the marker and the accompanying Source blockquote
 * from the body so admins can edit citations without hunting through HTML.
 */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { AuthContext } from '../../../App';
import { X, Quote } from 'lucide-react';

const CITE_RE = /\[cite:([a-zA-Z0-9\-_]+)\]/g;

const CitedSourcesChipStrip = ({ description, onChangeDescription }) => {
  const { API } = useContext(AuthContext);
  const [docsById, setDocsById] = useState({});

  const citedIds = useMemo(() => {
    const found = new Set();
    if (typeof description !== 'string') return [];
    let m;
    const re = new RegExp(CITE_RE.source, 'g');
    while ((m = re.exec(description))) found.add(m[1]);
    return Array.from(found);
  }, [description]);

  useEffect(() => {
    if (citedIds.length === 0) return;
    const missing = citedIds.filter((id) => !docsById[id]);
    if (missing.length === 0) return;
    let cancelled = false;
    axios.get(`${API}/admin/ai-knowledge/documents`).then((r) => {
      if (cancelled) return;
      const list = Array.isArray(r.data) ? r.data : (r.data?.documents || []);
      const next = { ...docsById };
      for (const d of list) next[d.id] = d;
      setDocsById(next);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [citedIds, API, docsById]);

  const removeCitation = (id) => {
    if (!onChangeDescription || typeof description !== 'string') return;
    // Strip the [cite:id] marker.
    let next = description.replace(new RegExp(`\\[cite:${id}\\]`, 'g'), '');
    // Best-effort: strip the accompanying Source blockquote if it wraps
    // this doc's title. We match a <blockquote>…Source: TITLE…</blockquote>
    // right before the marker location.
    const title = docsById[id]?.title || docsById[id]?.filename;
    if (title) {
      const escapedTitle = title.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const bqRe = new RegExp(`<blockquote>[\\s\\S]{0,400}?${escapedTitle}[\\s\\S]{0,120}?</blockquote>`, 'i');
      next = next.replace(bqRe, '');
    }
    onChangeDescription(next);
  };

  if (citedIds.length === 0) return null;

  return (
    <div className="rounded-lg border border-border bg-muted/30 p-3" data-testid="cited-sources-strip">
      <div className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2 inline-flex items-center gap-1">
        <Quote className="w-3 h-3" /> Cited sources ({citedIds.length})
      </div>
      <div className="flex flex-wrap gap-1.5">
        {citedIds.map((id) => {
          const d = docsById[id];
          // Preferred surface: "Author (Year) — Title" with Publisher and
          // link when the doc carries them. Falls back to the internal
          // KB title/filename for older docs that predate the metadata.
          const title    = d?.title || d?.filename || id.slice(0, 8);
          const authors  = d?.authors && String(d.authors).trim();
          const year     = d?.year && String(d.year).trim();
          const publisher= d?.publisher && String(d.publisher).trim();
          const url      = d?.original_url || d?.source_url || d?.url;
          const primary  = authors && year
            ? `${authors} (${year}) — ${title}`
            : authors
              ? `${authors} — ${title}`
              : year
                ? `${title} (${year})`
                : title;
          const secondary = publisher || (url ? new URL(url, 'https://x').hostname.replace(/^www\./, '') : '');
          return (
            <span
              key={id}
              className="inline-flex items-start gap-1 rounded-2xl bg-primary/10 text-primary border border-primary/30 pl-3 pr-1 py-1 text-xs font-bold max-w-full"
              data-testid={`cited-source-chip-${id}`}
              title={publisher ? `Publisher: ${publisher}` : id}
            >
              <span className="flex flex-col items-start leading-tight max-w-[260px]">
                <span className="truncate max-w-[260px]">{primary}</span>
                {(secondary || url) && (
                  <span className="text-[10px] font-medium opacity-80 truncate max-w-[260px]">
                    {secondary}
                    {url && (
                      <>
                        {secondary ? ' · ' : ''}
                        <a
                          href={url}
                          target="_blank"
                          rel="noreferrer noopener"
                          className="underline hover:no-underline"
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`cited-source-link-${id}`}
                        >
                          original source
                        </a>
                      </>
                    )}
                  </span>
                )}
              </span>
              <button
                type="button"
                onClick={() => removeCitation(id)}
                className="ml-1 rounded-full hover:bg-primary/20 h-5 w-5 inline-flex items-center justify-center self-start"
                title="Remove this citation from the lesson"
                data-testid={`cited-source-remove-${id}`}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          );
        })}
      </div>
    </div>
  );
};

export default CitedSourcesChipStrip;
